/**
 * `Toolbar` (W1-12 ui-kit-i18n; contract ToolbarProps): role="toolbar" with a roving tab stop (WAI-ARIA toolbar
 * pattern). Tab enters the toolbar once, on the last used item; arrow keys along the orientation move between
 * items (wrapping), Home / End jump to the ends. Disabled items (aria-disabled) stay reachable so their reason
 * can be read. A nested composite widget (a Segmented radio group, tabs) counts as one stop and keeps its own
 * arrow keys; text fields keep theirs too.
 */

import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import { focusableIn, isTextEntry } from './internal/focus';
import s from './Toolbar.module.css';
import type { ToolbarProps } from './types';

const COMPOSITE = '[role="radiogroup"],[role="tablist"],[role="listbox"],[role="menubar"],[role="grid"],[role="tree"]';

function compositeOf(root: HTMLElement, el: HTMLElement): HTMLElement | null {
  const c = el.parentElement?.closest<HTMLElement>(COMPOSITE) ?? null;
  return c !== null && c !== root && root.contains(c) ? c : null;
}

interface Stop {
  readonly el: HTMLElement;
  /** Inside a nested composite: its tab index belongs to that widget. */
  readonly nested: boolean;
}

/** The toolbar's stops in DOM order (one per nested composite widget). */
export function toolbarStops(root: HTMLElement): Stop[] {
  const stops: Stop[] = [];
  const seen = new Set<HTMLElement>();
  for (const el of focusableIn(root)) {
    // A composite widget's own container (Radix makes it focusable to forward focus) is represented by its items.
    if (el.matches(COMPOSITE)) continue;
    const composite = compositeOf(root, el);
    if (composite === null) {
      stops.push({ el, nested: false });
      continue;
    }
    if (seen.has(composite)) continue;
    seen.add(composite);
    const inner = focusableIn(composite);
    const rep = inner.find((x) => x.tabIndex >= 0) ?? inner[0];
    if (rep !== undefined) stops.push({ el: rep, nested: true });
  }
  return stops;
}

export function Toolbar(props: ToolbarProps & { readonly className?: string | undefined }) {
  const tr = useTr();
  const ref = useRef<HTMLDivElement>(null);
  const current = useRef<HTMLElement | null>(null);
  const orientation = props.orientation ?? 'horizontal';

  useEffect(() => {
    const root = ref.current;
    if (root === null) return;
    const apply = (): void => {
      const stops = toolbarStops(root);
      const own = stops.filter((x) => !x.nested);
      const active = stops.find((x) => x.el === current.current) ?? stops[0];
      for (const stop of own) stop.el.tabIndex = stop === active ? 0 : -1;
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    const onFocusIn = (e: FocusEvent): void => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const stop = toolbarStops(root).find(
        (x) => x.el === target || x.el.parentElement?.closest(COMPOSITE)?.contains(target) === true,
      );
      if (stop === undefined) return;
      current.current = stop.el;
      apply();
    };
    root.addEventListener('focusin', onFocusIn);
    return () => {
      observer.disconnect();
      root.removeEventListener('focusin', onFocusIn);
    };
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    const root = ref.current;
    if (root === null) return;
    const target = e.target instanceof HTMLElement ? e.target : null;
    if (target !== null && isTextEntry(target)) return;
    const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    if (![prevKey, nextKey, 'Home', 'End'].includes(e.key)) return;
    const stops = toolbarStops(root);
    if (stops.length === 0) return;
    let index = stops.findIndex(
      (x) =>
        x.el === target ||
        (target !== null &&
          compositeOf(root, target) !== null &&
          x.nested &&
          compositeOf(root, x.el) === compositeOf(root, target)),
    );
    if (index < 0) index = 0;
    let next: number;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = stops.length - 1;
    else if (e.key === nextKey) next = (index + 1) % stops.length;
    else next = (index - 1 + stops.length) % stops.length;
    const stop = stops[next];
    if (stop === undefined) return;
    e.preventDefault();
    current.current = stop.el;
    for (const x of stops) if (!x.nested) x.el.tabIndex = x === stop ? 0 : -1;
    stop.el.focus();
  };

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={tr(props.label)}
      aria-orientation={orientation}
      className={cx(s.toolbar, orientation === 'vertical' && s.vertical, props.className)}
      onKeyDown={onKeyDown}
    >
      {props.children}
    </div>
  );
}

/** A visual divider between groups of toolbar items (not a tab stop). */
export function ToolbarSeparator() {
  return <span className={s.separator} role="separator" />;
}
